import React from 'react';
import { colorPalette } from '@sharefiledev/antd-config';
import { Close, RecycleBin } from '@sharefiledev/icons';
import { Button, Divider, Flex, theme, Typography } from 'antd';
import {
	ComponentTypes,
	Field,
	getAllFields,
	getComponentType,
} from '../../../common/componentUtils';
import { useGetScreenSize } from '../../../common/hooks/useGetScreenSize';
import { useAppStore } from '../../../common/store';
import { ComponentInputType, ICON_SIZE } from '../../../constants';
import { t } from '../../../utils';
import { AnnotationsEditor } from './AnnotationsEditor';
import { SignerFieldsEditor } from './SignerFieldsEditor';
import { SystemFilledFieldsEditor } from './SystemFilledFieldsEditor';

const { Title } = Typography;

export const ComponentEditor = ({
	componentsToEdit,
	onClose,
	handleComponentUpdate,
	variables,
	addCheckbox,
	removeCheckbox,
	checkboxGroupComponents,
	onComponentSelect,
}) => {
	const { isExtraExtraLarge } = useGetScreenSize();

	const { token } = theme.useToken();
	const componentForEdit = componentsToEdit[0];
	const fields: Field[] = getAllFields();
	const requiredField = fields.find(f => f.type === componentForEdit?.type);
	const Icon: React.FC<any> = requiredField?.icon;
	const { documentTemplate } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
	}));

	const handleComponentDelete = () => {
		handleComponentUpdate(
			componentForEdit.type === ComponentTypes.CheckGroupComponent
				? checkboxGroupComponents
				: [componentForEdit],
			'delete'
		);
		onClose();
	};

	const componentInputType = getComponentType(componentForEdit);

	const pad = isExtraExtraLarge ? 0 : 8;

	return (
		<Flex vertical style={{ width: '280px', margin: '0px auto', height: '100%' }}>
			<Flex align="center" justify="space-between">
				{Icon && (
					<Flex
						style={{
							paddingLeft: pad,
						}}
					>
						<Icon size={16} />
					</Flex>
				)}

				<Title level={5}>{requiredField?.label}</Title>
				<Button
					type="text"
					icon={<Close size={ICON_SIZE} />}
					onClick={() => onClose()}
					aria-label="close"
					size="small"
				/>
			</Flex>
			<Divider style={{ margin: token.marginXXS }} />
			<Flex
				vertical
				style={{
					padding: pad,
				}}
			>
				{componentInputType === ComponentInputType.Signer && (
					<SignerFieldsEditor
						componentsToEdit={componentsToEdit}
						documentTemplate={documentTemplate}
						addCheckbox={addCheckbox}
						removeCheckbox={removeCheckbox}
						checkboxGroupComponents={checkboxGroupComponents}
						handleComponentUpdate={handleComponentUpdate}
						onComponentSelect={onComponentSelect}
					/>
				)}
				{componentInputType === ComponentInputType.System && (
					<SystemFilledFieldsEditor component={componentForEdit} variables={variables} />
				)}
				{componentInputType === ComponentInputType.Annotation && (
					<AnnotationsEditor
						componentsToEdit={componentsToEdit}
						handleComponentUpdate={handleComponentUpdate}
					/>
				)}
			</Flex>
			<Flex vertical style={{ marginTop: 'auto', marginBottom: token.marginSM }}>
				<Button
					type="text"
					icon={<RecycleBin color={colorPalette.lavender7} size={ICON_SIZE} />}
					onClick={handleComponentDelete}
					block
					style={{ display: 'flex', alignItems: 'center', gap: token.marginXS }}
				>
					{t('removeFieldLabel')}
				</Button>
			</Flex>
		</Flex>
	);
};
